import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import ProductGroup from '../../../../../components/common/ProductGroup';
import CarItemCodeDropdown from '../../../../../components/common/CarItemCodeDropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const WarrantyPeriodForm = ({ visible, onHide, onSubmit, initialData, loading }) => {
    const { th } = useLanguage();
    const [formData, setFormData] = useState({
        id: null,
        selectedWarrantyPeriod: null,
        productGroup: '',
        carItemCode: '',
        startDate: null,
        endDate: null,
        modelYear: 0,
        warrantyPeriod: 0,
        warrantyKmInfo: 0,
        status: 0
    });
    const toast = useRef(null);
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        if (visible && !initialData) {
            setFormData({
                id: null,
                selectedWarrantyPeriod: null,
                productGroup: '',
                carItemCode: '',
                startDate: null,
                endDate: null,
                modelYear: 0,
                warrantyPeriod: 0,
                warrantyKmInfo: 0,
                status: 0
            });
        }
    }, [visible, initialData]);

    useEffect(() => {
        if (initialData) {
            setLocalSubmitted(false);
            const fixedStartDate = typeof initialData.startDate === 'string' ? new Date(initialData.startDate) : initialData.startDate;

            const fixedEndDate = typeof initialData.endDate === 'string' ? new Date(initialData.endDate) : initialData.endDate;

            setFormData({
                ...initialData,
                startDate: fixedStartDate,
                endDate: fixedEndDate
            });
        }
    }, [initialData]);

    const handleSubmit = (e) => {
        setLocalSubmitted(true);

        const validationErrors = [];

        if (!formData.startDate) {
            validationErrors.push(th('vehicleWarrantyPeriod.validationMessages.startDateRequired'));
        }

        if (!formData.productGroup) {
            validationErrors.push(th('vehicleWarrantyPeriod.validationMessages.productGroupRequired'));
        }

        if (validationErrors.length > 0) {
            validationErrors.forEach((error, index) => {
                setTimeout(() => {
                    toast.current?.show({
                        severity: 'error',
                        summary: th('vehicleWarrantyPeriod.validationMessages.missingFieldSummary'),
                        detail: error,
                        life: 4000
                    });
                }, index * 200);
            });
            return;
        }

        e.preventDefault();

        const payload = {
            ...formData
        };

        onSubmit(payload);
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleProductGroupChange = (productGroup) => {
        setFormData((prev) => ({
            ...prev,
            productGroup: productGroup || ''
        }));
    };
    const handleCarItemCodeChange = (productGroup) => {
        setFormData((prev) => ({
            ...prev,
            carItemCode: String(formData.carItemCode || '')
        }));
    };

    const renderFooter = () => {
        return (
            <div>
                <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined />
                <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
            </div>
        );
    };

    return (
        <Dialog visible={visible} footer={renderFooter()} style={{ width: '25vw' }} header={th('vehicleWarrantyPeriod.formTitle')} modal className="p-fluid" onHide={onHide}>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit} className="p-fluid">
                <div className="grid">
                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="startDate">{th('vehicleWarrantyPeriod.formLabels.startDate')}</label>
                            <Calendar id="startDate" value={formData.startDate} onChange={(e) => handleInputChange('startDate', e.value)} dateFormat="dd/mm/yy" showIcon placeholder={th('vehicleWarrantyPeriod.placeholders.startDate')} />
                        </div>
                        {localSubmitted && !formData.startDate && <small className="p-error">{th('vehicleWarrantyPeriod.validationMessages.startDateRequired')}</small>}
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="endDate">{th('vehicleWarrantyPeriod.formLabels.endDate')}</label>
                            <Calendar
                                id="endDate"
                                value={formData.endDate}
                                minDate={formData.startDate}
                                onChange={(e) => handleInputChange('endDate', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                placeholder={th('vehicleWarrantyPeriod.placeholders.endDate')}
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="productGroup">{th('vehicleWarrantyPeriod.formLabels.productGroup')}</label>
                            <ProductGroup value={formData.productGroup} onChange={handleProductGroupChange} />
                        </div>
                        {localSubmitted && !formData.productGroup && <small className="p-error">{th('vehicleWarrantyPeriod.validationMessages.productGroupRequired')}</small>}
                    </div>
                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="carItemCode">{th('vehicleWarrantyPeriod.formLabels.carItemCode')}</label>
                            <CarItemCodeDropdown value={formData.carItemCode} onChange={(value) => setFormData({ ...formData, carItemCode: value })} productGroup={formData.productGroup} disabled={!formData.productGroup} />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="modelYear">{th('vehicleWarrantyPeriod.formLabels.modelYear')}</label>
                            <InputNumber id="modelYear" value={formData.modelYear} onValueChange={(e) => handleInputChange('modelYear', e.value)} mode="decimal" minFractionDigits={0} maxFractionDigits={0} />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="warrantyPeriod">{th('vehicleWarrantyPeriod.formLabels.warrantyPeriod')}</label>
                            <InputNumber id="warrantyPeriod" value={formData.warrantyPeriod} onValueChange={(e) => handleInputChange('warrantyPeriod', e.value)} mode="decimal" minFractionDigits={0} maxFractionDigits={0} />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="warrantyKmInfo">{th('vehicleWarrantyPeriod.formLabels.warrantyKmInfo')}</label>
                            <InputNumber id="warrantyKmInfo" value={formData.warrantyKmInfo} onValueChange={(e) => handleInputChange('warrantyKmInfo', e.value)} mode="decimal" minFractionDigits={0} maxFractionDigits={0} />
                        </div>
                    </div>
                </div>
            </form>
        </Dialog>
    );
};
export default WarrantyPeriodForm;
